'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  DynamicForm,
  AssignmentInd,
  Tab,
  AccountTree,
  Source,
  Rule,
  Balance,
  Lightbulb,
  TrackChanges,
  NotificationsActive,
  BarChart,
  PictureAsPdf,
  PhoneAndroid,
  CheckCircle,
  WarningAmber,
  ExpandMore,
  VerifiedUser,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import BrandName from '../../components/BrandName';
import WorkflowStepper from '../../components/WorkflowStepper';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  { title: 'Report the Incident', description: 'Capture on configurable intake form' },
  { title: 'Assign', description: 'Route to user, role, or committee with due date' },
  { title: 'Investigate', description: 'Work guided tabs, evidence, and RCA' },
  { title: 'Conclude', description: 'Document findings, substantiation, avoidability' },
  { title: 'Act & Report', description: 'Action plans, PDF reports, QAPI integration' }
];

const painPoints = [
  'Incident reports on paper forms that vary by shift and never match the investigation file',
  'No clear ownership—who is investigating, what\'s due, and what\'s overdue is invisible',
  'Witness statements, photos, and evidence scattered across email and shared drives',
  'Root cause analysis done verbally with nothing documented to stand behind',
  'Substantiation and avoidability decisions made without a structured record',
  'Findings that never become corrective actions—and conclusions surveyors can pick apart',
];

const capabilities = [
  {
    icon: DynamicForm,
    title: 'Configurable Incident Intake',
    description: 'Capture incidents on intake forms built in the Form Wizard—configured to your facility\'s needs, not a vendor\'s fixed template. Track incident type and sub-type (falls, medication errors, abuse and neglect allegations, elopement, and more), incident and reported date-times, resident details, and a facility incident identifier.',
  },
  {
    icon: AssignmentInd,
    title: 'Assignment to User, Role, or Committee',
    description: 'Assign each incident—and each investigation form—to a specific user, an entire role, or a committee, with an assigned date, due date, and priority. Everyone knows whose case it is and when it\'s due.',
  },
  {
    icon: Tab,
    title: 'Guided Six-Tab Investigation Workspace',
    description: 'Every investigation runs through a consistent workspace: incident details, investigation forms, document management, root cause analysis, event conclusion, and process guidelines—so no case skips a step.',
  },
  {
    icon: AccountTree,
    title: 'Root Cause Analysis',
    description: 'Run documented RCA with 5 Whys and fishbone tools, including templates by incident type. RCA results can flow forward into the event conclusion so findings and analysis stay connected.',
  },
  {
    icon: Source,
    title: 'Evidence & Document Management',
    description: 'Upload photos, videos, witness statements, and reports directly to the investigation—one evidence file per case instead of attachments scattered across inboxes.',
  },
  {
    icon: Rule,
    title: 'Investigation Process Guidelines',
    description: 'Configure investigation guidelines by incident type—including abuse allegation templates—with completion criteria such as required forms, documents, RCA, and substantiation. Investigators see exactly what a complete investigation requires.',
  },
  {
    icon: Balance,
    title: 'Substantiation & Avoidability Determinations',
    description: 'Close each case with structured determinations: substantiation with comments, incident avoidability with rationale, and an investigation effectiveness rating—decisions documented, not implied.',
  },
  {
    icon: Lightbulb,
    title: 'Event Conclusion & Lessons Learned',
    description: 'Document the investigation summary, immediate and corrective actions taken, preventive measures, key lessons learned, recommendations, follow-up requirements with a responsible person, and closure approval.',
  },
  {
    icon: TrackChanges,
    title: 'Action Plans from Findings',
    description: 'Create corrective action plans directly from the investigation—with AI-suggested actions available—assigned with due dates and tracked through completion in the platform\'s action plan workflow.',
  },
  {
    icon: NotificationsActive,
    title: 'Notification Rules & Alerts',
    description: 'Configure notification rules that alert the right roles based on incident attributes—type, severity, injury, medical treatment, state-reportable status, 911 calls, and more—plus automatic notifications on investigation status changes.',
  },
  {
    icon: BarChart,
    title: 'Dashboards & Metrics',
    description: 'The investigation dashboard tracks total, completed, pending, and overdue investigations, average days to start and complete, completion rate, and avoidable rate—with breakdowns by status and investigator and Excel export.',
  },
  {
    icon: PictureAsPdf,
    title: 'Investigation Report PDFs & QAPI Integration',
    description: 'Generate a professional investigation report PDF, and push selected investigation content into your QAPI reporting via the Document Vault—evidence ready for committee review and survey.',
  },
  {
    icon: PhoneAndroid,
    title: 'Mobile Access',
    description: 'Report incidents, work all six investigation tabs, and review dashboards from the CareAscendium mobile app—so investigations keep moving from the floor, not just the office.',
  },
];

const roles = [
  {
    title: 'Administrators & DONs',
    benefit: 'Real-time visibility into every open investigation, with overdue and avoidable-rate metrics',
  },
  {
    title: 'Investigators',
    benefit: 'One guided workspace with forms, evidence, RCA, and clear completion criteria',
  },
  {
    title: 'Frontline staff',
    benefit: 'Fast, configurable incident reporting on web or mobile',
  },
  {
    title: 'QAPI & quality leaders',
    benefit: 'Investigation findings flowing into QAPI reports and corrective action plans',
  },
  {
    title: 'Corporate / multi-site operators',
    benefit: 'Consistent investigation process and guidelines across every facility',
  },
];

const integrations = [
  'Action Plans',
  'QAPI & Doc Vault',
  'Committees',
  'Form Wizard',
  'Due Items',
  'Notifications',
];

const outcomes = [
  { value: 'Speed', label: 'Investigation timeliness', detail: 'Average days to start and complete, with overdue counts' },
  { value: '100%', label: 'Completion rate', detail: 'Completed versus pending investigations on a live dashboard' },
  { value: 'Low', label: 'Avoidability insight', detail: 'Avoidable rate trends to focus prevention efforts' },
  { value: 'Clear', label: 'Documented determinations', detail: 'Substantiation and avoidability recorded on every closed case' },
  { value: 'Done', label: 'Corrective follow-through', detail: 'Action plans created from findings and tracked to completion' },
  { value: 'Ready', label: 'Survey readiness', detail: 'Structured case files with RCA, evidence, and PDF reports' },
];

const complianceItems = [
  'Configurable incident types including abuse and neglect allegation categories',
  'Investigation guidelines with completion criteria—including abuse-specific templates',
  'Documented root cause analysis with recognized tools and incident-type templates',
  'Structured substantiation, avoidability, and effectiveness determinations',
  'State-reportable flagging and notification rules tied to incident attributes',
  'Complete case files with evidence, conclusions, action plans, and PDF reports for survey',
];

const faqs = [
  {
    q: 'Can we customize what\'s captured when an incident is reported?',
    a: 'Yes. Incident intake runs on Form Wizard forms you configure—your fields, your sections, your incident types and sub-types—rather than a fixed vendor template.',
  },
  {
    q: 'How does the investigation stay consistent across cases?',
    a: 'Every investigation uses the same six-tab workspace, and configurable process guidelines define completion criteria by incident type—so an abuse allegation investigation, for example, follows your defined protocol every time.',
  },
  {
    q: 'Is root cause analysis built in?',
    a: 'Yes. Investigators run documented RCA with 5 Whys and fishbone tools, including templates by incident type, and the analysis flows into the event conclusion.',
  },
  {
    q: 'How are witness statements and evidence handled?',
    a: 'Photos, videos, witness statements, and reports upload directly to the investigation\'s document management tab—one organized evidence file per case.',
  },
  {
    q: 'How do findings turn into action?',
    a: 'Action plans are created directly from the investigation—with AI-suggested actions available—assigned to owners with due dates, and tracked through the platform\'s action plan workflow and dashboards.',
  },
  {
    q: 'Can leadership see where every investigation stands?',
    a: 'Yes. The investigation dashboard shows totals, completion rate, overdue investigations, average days to start and complete, and avoidable rate—with breakdowns and Excel export.',
  },
  {
    q: 'Does it work on mobile?',
    a: 'Yes. Incident reporting, the full six-tab investigation, and dashboards are available in the CareAscendium mobile app on iOS and Android.',
  },
];

export default function IncidentInvestigationClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Incident Investigation Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Incident Investigation"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            From Incident Intake to Substantiated Conclusion—One Structured Investigation Workspace
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> gives skilled nursing facilities a complete incident lifecycle—configurable
            intake forms, guided six-tab investigations with root cause analysis, evidence
            management, substantiation and avoidability determinations, action plans, and
            QAPI-ready reports—on web and mobile.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with Paper Incident Files
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              When something happens to a resident, the clock starts. In most SNFs, the incident gets
              a paper form, the investigation lives in someone's email, witness statements sit in a
              drawer, and the root cause analysis—if it happens—is a conversation. Proving the
              facility investigated thoroughly, concluded appropriately, and acted on findings
              becomes a scramble when surveyors or families ask.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;An investigation you can't document is an investigation that didn't happen—no matter how thorough it actually was.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Workflow. From Report to Resolution.
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              <BrandName variant="light" style={{ fontSize: 'inherit' }} /> connects incident intake, investigation, root cause analysis, evidence,
              conclusion, and corrective action in a single auditable workspace. Configurable intake
              forms capture each incident your way; a guided six-tab investigation keeps every case
              structured; and dashboards show leadership exactly where every investigation stands.
            </Typography>
          </Box>
          <WorkflowStepper steps={workflowSteps} />
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  width: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Platform
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                  Incident Investigation connects natively to the rest of your quality and compliance work:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Investigations that hold up.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto',
          }}
        >
          {outcomes.map((metric) => (
            <Paper key={metric.label} elevation={2} sx={{ p: 3, textAlign: 'center', borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h3" sx={{ color: BRAND_GREEN, fontWeight: 800, mb: 1, fontSize: '2rem' }}>
                {metric.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: '#333', mb: 1 }}>{metric.label}</Typography>
              <Typography sx={{ color: '#666', fontSize: '0.9rem', lineHeight: 1.5, mt: 'auto' }}>
                {metric.detail}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Compliance & Quality */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Paper elevation={3} sx={{ p: { xs: 4, md: 5 }, borderRadius: 3, borderTop: `6px solid ${BRAND_BLUE}`, textAlign: 'center' }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <VerifiedUser sx={{ fontSize: 48, color: BRAND_BLUE, mb: 2 }} />
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
                Documentation You Can Stand Behind
              </Typography>
              <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.6 }}>
                <BrandName variant="light" style={{ fontSize: 'inherit' }} /> supports the investigative rigor SNFs need around resident incidents:
              </Typography>
            </Box>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, width: 'fit-content', maxWidth: '100%', mx: 'auto' }}>
              {complianceItems.map((item) => (
                <Box component="li" key={item} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 2 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#444', fontWeight: 500, lineHeight: 1.5, textAlign: 'left' }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expanded === index}
                onChange={handleChange(index)}
                sx={{
                  mb: 2.5,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 3, pt: 0 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, fontSize: '0.95rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Standardize Every Investigation?
            </Typography>
            <Typography sx={{ mb: 4, fontSize: '1.1rem', maxWidth: 700, mx: 'auto', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> helps SNFs investigate thoroughly, conclude defensibly, and turn findings into corrective action—with a complete record at every step.
            </Typography>
            <Box>
              <Link href="/contact" style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: BRAND_GREEN,
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#55901d' },
                  }}
                >
                  REQUEST A DEMO
                </Button>
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
